import axios from 'axios'
import { identity, memoizeWith } from 'ramda'
import React, { useState } from 'react'
import { useEffect } from 'react'

import { getGeoIP } from '../../../../../common/js/api/geoip'
import { IRegion, parseUnits, Units, withSlash } from './helpers'

declare const SI_REGIONS: IRegion[]

const currencyCmp = (currencyCode: string) => (
  a: IRegion,
  b: IRegion
): number => {
  const aHasIt = a.currencyCodes.map((cc) => cc.code).includes(currencyCode)
  const bHasIt = b.currencyCodes.map((cc) => cc.code).includes(currencyCode)

  if (aHasIt && bHasIt) {
    const aHasGBP = a.currencyCodes.map((cc) => cc.code).includes('GBP')
    const bHasGBP = b.currencyCodes.map((cc) => cc.code).includes('GBP')

    if (aHasGBP && bHasGBP) {
      return a.countryCode.localeCompare(b.countryCode)
    }

    if (aHasGBP) {
      return -1
    }

    return 1
  }

  if (aHasIt && !bHasIt) {
    return -1
  }

  if (!aHasIt && bHasIt) {
    return 1
  }

  return 0
}

const getRegions = memoizeWith(identity as any, () => {
  const regionsPromise =
    SI_REGIONS && SI_REGIONS.length > 0
      ? Promise.resolve(SI_REGIONS)
      : axios
          .get<IRegion[]>('/wp-json/stickerit/v1/regions')
          .then((resp) => resp.data)

  return regionsPromise
})

const getUnits = (): Units => localStorage.getItem('si_units') as Units
const getCurrencyCode = () => localStorage.getItem('si_currencyCode')
const getShipToCountryCode = () => {
  const found = localStorage.getItem('si_shipToCountryCode')

  if (found && found === 'null') {
    return null
  }

  return found
}
const getTax = () => localStorage.getItem('si_tax')
const getCartPath = () => localStorage.getItem('si_cartPath')
export const getPath = () => localStorage.getItem('si_path')
const getFlagImage = () => localStorage.getItem('si_flagImage')

export const useRegion = () => {
  const [regionChooserDismissed, setRegionChooserDismissed] = useState(
    localStorage.getItem('si_regionChooserDismissed') === 'true'
  )

  const [ready, setReady] = useState(false)
  const [units, lsetUnits] = useState<Units>(getUnits() || 'cm')
  const [currencyCode, lsetCurrencyCode] = useState(getCurrencyCode())
  const [shipToCountryCode, lsetShipToCountry] = useState(
    getShipToCountryCode()
  )
  const [pricesIncludeVAT, lsetPricesIncludeVAT] = useState(
    getTax() !== 'ex_vat'
  )
  const [cartPath, lsetCartPath] = useState(getCartPath() || '/cart')
  const [path, lsetPath] = useState(getPath() || '/')
  const [flagImage, lsetFlagImage] = useState(getFlagImage())
  const [allRegions, setAllRegions] = useState<IRegion[]>([])
  const [geoCountryCode, setGeoCountryCode] = useState<string>(null)
  const [matchedRegion, setMatchedRegion] = useState<IRegion>(null)

  useEffect(() => {
    const geo = getGeoIP()
    const regionsPromise = getRegions()

    Promise.all([geo, regionsPromise])
      .then(([geo, regions]) => {
        setGeoCountryCode(geo)
        setAllRegions(regions)

        const url = window.location.pathname
        const urlSlash = withSlash(url)

        const exactPathMatches = regions
          .filter((r) => r.path === urlSlash)
          .sort((a, b) => {
            const res = currencyCmp(currencyCode)(a, b)

            if (res !== 0) {
              return res
            }

            return 0
          })
        const prefixMatches = regions
          .filter((r) => urlSlash.startsWith(r.path))
          .sort((a, b) => {
            const pathCmp = b.path.length - a.path.length

            if (pathCmp !== 0) {
              return pathCmp
            }

            const res = currencyCmp(currencyCode)(a, b)

            if (res !== 0) {
              return res
            }

            return 0
          })

        let matched: IRegion

        if (exactPathMatches.length > 0) {
          matched = exactPathMatches[0]
        }

        if (prefixMatches.length > 0) {
          matched = prefixMatches[0]
        }

        if (!matched) {
          return
        }

        if (matched) {
          setMatchedRegion(matched)
        }

        if (units === null) {
          lsetUnits(parseUnits(matched.units))
        }

        if (currencyCode === null) {
          lsetCurrencyCode(matched.currencyCodes[0].code)
        }

        if (shipToCountryCode === null) {
          lsetShipToCountry(matched.countryCode)
        }

        if (flagImage === null) {
          lsetFlagImage(matched.flagImage)
        }

        if (path === null) {
          lsetPath(matched.path)
        }

        if (cartPath === null) {
          lsetCartPath(matched.cartPath)
        }

        if (pricesIncludeVAT === null) {
          lsetPricesIncludeVAT(matched.tax === 'inc_vat')
        }
      })
      .then(() => {
        setReady(true)
      })
  }, [])

  const result = {
    ready,
    units,
    currencyCode,
    shipToCountryCode,
    pricesIncludeVAT,
    cartPath,
    allRegions,
    geoCountryCode,
    flagImage,
    regionChooserDismissed,
    path,
    languageCode: matchedRegion?.languageCode || 'en-gb',
  }

  return result
}
